import _get from 'lodash/get';
import _unset from 'lodash/unset';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import RestClient from '../RestClient/RestClient';
import LoggerHelper from '../logs/LoggerHelper';
import { cstToUTC, getChannel } from '../Common/CommonHelper';
import IrisPageConstants from './IrisPageConstants';
import { apiHosts } from '../RestClient/FetchHelper.config';
import { getCookie } from '../helpers/Cookies';

const { AGGREGATOR_API, PRV_AGGREGATOR_API, CONTENT_API, PRV_CONTENT_API } = IrisPageConstants;

/** Logic for adding CacheControl to the response */
const deriveIrisObject = (json, isfallbackResponse, fallbackTTL) => {
    let cacheControl = 0;
    const responseHeadersToProxy = [];

    // picking up the amount of time page should be cached at akamai level
    /* istanbul ignore if */
    if (__SERVER__) {
        try {
            const edgeCacheTagName = 'edge-cache-tag';
            const edgeCacheTagValue = _get(json, `headers._headers[${edgeCacheTagName}][0]`, '');
            if (edgeCacheTagValue) {
                responseHeadersToProxy.push({ name: edgeCacheTagName, value: edgeCacheTagValue });
            }
            if (isfallbackResponse) {
                cacheControl = fallbackTTL || 10;
            } else {
                const utcCacheAge =
                    json &&
                    json.headers &&
                    json.headers._headers &&
                    json.headers._headers['x-yoda-cache-control'];
                if (utcCacheAge) {
                    const current = new Date();
                    const future = new Date(utcCacheAge);
                    cacheControl = (future - current) / 1000;
                }
            }
        } catch (e) {
            cacheControl = 0;
        }
    }

    return {
        data: json.data,
        cacheControl,
        responseHeadersToProxy,
        status: json.status,
        statusText: json.statusText,
    };
};

/**
 * Trims unused attributes from IRIS API response.
 * @param {*} contentItem
 */
export function hotdealsPostProcessor(contentItem) {
    if (contentItem.componentType !== 'hotdeals') return;

    const productGrid = _get(contentItem, 'content.productGrid');
    _unset(productGrid, 'contentExpression');

    const productGridItems = _get(productGrid, 'items');
    _forEach(productGridItems, (productGridItem) => {
        const images = productGridItem.images || [];
        const image = _find(images, (item) => item.type === 'PRIMARY') || images[0] || {};

        productGridItem.images = [image];

        // for each item of productGrid unset
        _unset(productGridItem, 'dimensions');
        _unset(productGridItem, 'pricing.lots');
        _unset(productGridItem, 'pricing.href');
        _unset(productGridItem, 'pricing.items');
    });
}

/**
 * As of now supports one filter to be called for each component
 * @param {} json
 * @param {*} postPreProcessor
 */
const pipePostProcessor = (json, postPreProcessor) => {
    if (!postPreProcessor) {
        // If not filter return immediately
        return json;
    }
    // Get all content row nodes and iterate
    const rows = _get(json, 'data.content.rows');
    _forEach(rows, (rowsItem) => {
        // for each row iterate columns
        const cols = _get(rowsItem, 'columns');
        _forEach(cols, (colsItem) => {
            // for each cols iterate components
            const comps = _get(colsItem, 'components');
            _forEach(comps, (compsItem) => {
                _unset(compsItem, 'publishingChannels');
                _unset(compsItem, 'id');
                _unset(compsItem, 'displayName');
                // for each content get productGrid
                try {
                    postPreProcessor(compsItem);
                } catch (e) {
                    /* istanbul ignore next */
                    const logger = LoggerHelper.getLogger();
                    logger.warn({ message: `IRIS API Transfor error [${e.message}]` });
                }
            });
        });
    });
    return json;
};

export const getStaticContent = (fallbackPreferences, postProcessor, json) => {
    const fallbackTTL = _get(fallbackPreferences, 'ttl', 10);
    const fallbackURL = _get(fallbackPreferences, 'url', '');
    return RestClient.get({
        url: fallbackURL,
        external: true,
    }).then((fallbackRes) => {
        const responseStatus = _get(fallbackRes, 'status', 0);
        return responseStatus === 0 && json
            ? deriveIrisObject(pipePostProcessor(json, postProcessor), true, fallbackTTL)
            : deriveIrisObject(pipePostProcessor(fallbackRes, postProcessor), true, fallbackTTL);
    });
};

export const getPageAggregator = (data, postProcessor, enableIrisFallback, fallbackPreferences) => {
    /* istanbul ignore next */
    const aggregator = data.aggregateAPI ? AGGREGATOR_API : CONTENT_API;
    let url = `${aggregator}${data.params}`;
    const context = RestClient.getContext();
    const channel = getChannel(context); // getting the channel type
    let previewDate = '';

    /*
     * if preview env and cookie is set
     * then add config val previewDate
     * FetchHelper would set the headers
     */
    /* istanbul ignore next */
    const prevAggregator = data.aggregateAPI ? PRV_AGGREGATOR_API : PRV_CONTENT_API;
    if (context.isPreview) {
        const dateValue = cstToUTC(getCookie('DP_PREVIEW_DATE'), context);
        // Check for the Preview Environment Date
        previewDate = (dateValue || new Date()).toISOString();
        url = `${prevAggregator}${data.params}`; // different end point to preveiew env
    }

    const config = {
        url,
        apiHost: apiHosts.content,
        excludeVersion: true,
        isXCloudHeaderRequired: true,
        credentials: 'include',
        encode: false,
        channel,
        previewDate,
        enableRequestHeaderCookie: true,
    };
    return RestClient.get(config)
        .then((json) => {
            const logger = LoggerHelper.getLogger();
            const device = Object.keys(context.deviceType).filter((key) => context.deviceType[key]);
            const responseCache = _get(json, 'headers._headers[cache-control');
            const jsonRes = {
                ...json,
                data: {
                    ...json.data,
                    yodaCacheControl: __SERVER__
                        ? _get(json, "headers._headers['x-yoda-cache-control']", [])[0]
                        : null,
                },
            };
            logger.info({
                message: 'IRIS API Response',
                reqId: context.reqId,
                deviceType: `${device}`,
                url: `${url}`,
                timestamp: new Date(),
                cache: `${responseCache}`,
            });
            const fallbackURL = _get(fallbackPreferences, 'url', '');
            /* istanbul ignore if */
            if (enableIrisFallback && jsonRes.status > 299 && fallbackURL) {
                return getStaticContent(fallbackPreferences, postProcessor, jsonRes).then(
                    (res) => res
                );
            }
            return deriveIrisObject(pipePostProcessor(jsonRes, postProcessor));
        })
        .catch(
            /* istanbul ignore next */
            (error) => {
                console.log(`request failed ${error}`);
            }
        );
};

export const getDepartmentS1 = (requestURL) => {
    const context = RestClient.getContext();
    const contentUrl = `/content-aggregator/shared-components/zones/S1${requestURL}`;
    const channel = getChannel(context); // getting the channel type
    const config = {
        url: contentUrl,
        apiHost: apiHosts.content,
        excludeVersion: true,
        credentials: 'include',
        isXCloudHeaderRequired: true,
        channel,
    };

    return RestClient.get(config)
        .then((json) => json)
        .catch((error) => {
            console.log(`request failed ${error}`);
        });
};
